import dayjs from 'dayjs'
import { XCircleIcon } from 'lucide-react'
import { type DeepPartial, type FieldErrors } from 'react-hook-form'

import { Button } from '@/components/button/button'
import { Card, CardContent } from '@/components/card/card'
import { DatePicker } from '@/components/date-picker/date-picker'
import { Form, FormField, FormItem, FormLabel } from '@/components/form/form'
import { Input } from '@/components/input/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/select/select'

import { cn } from '@/lib/utils'

import { type CaseAlertForm, type CaseAlertFormSchema } from '../../../types'
import { useSetAlertsCard } from './use-set-alerts-card'

export interface SetAlertsCardProps {
  defaultValues: CaseAlertForm
  onValueChange: (value: CaseAlertFormSchema) => void
  onDeleteCard: VoidFunction
  onError: (errors: FieldErrors<DeepPartial<CaseAlertFormSchema>>) => void
}

export const SetAlertsCard = ({ defaultValues, onValueChange, onDeleteCard, onError }: SetAlertsCardProps) => {
  const { form, formErrors, markAlertTypeError, markAlertDateError, markCountError, markUnitError } = useSetAlertsCard({
    defaultValues,
    onValueChange,
    onError,
  })

  return (
    <Card>
      <CardContent className={cn('flex flex-col', 'p-6')}>
        {/* Card delete button */}
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={cn('self-end', 'relative bottom-2 left-2')}
          onClick={onDeleteCard}
        >
          <XCircleIcon size={24} />
        </Button>
        {/* form */}
        <Form {...form}>
          <div className={cn('space-y-4')}>
            {/* Alert Type */}
            <FormField
              control={form.control}
              name="alertType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Alert Type</FormLabel>
                  <Input
                    {...field}
                    placeholder="e.g. RFE Response Due, Interview, Expire"
                    onChange={(e) => {
                      field.onChange(e)
                      markAlertTypeError(e.target.value)
                    }}
                  />
                </FormItem>
              )}
            />

            {/* Alert Target Date */}
            <FormField
              control={form.control}
              name="alertDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date</FormLabel>
                  <DatePicker
                    placeholder="MM-DD-YYYY"
                    disabledDateRule={{
                      before: dayjs(new Date()).add(1, 'day').toDate(),
                    }}
                    value={field.value}
                    onSelect={(date) => {
                      field.onChange(date)
                      markAlertDateError(date)
                    }}
                  />
                </FormItem>
              )}
            />

            {/* Notification Settings */}
            <FormField
              control={form.control}
              name="notificationSettings"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notification Settings</FormLabel>
                  <div className={cn('flex items-center space-x-2')}>
                    <span className={cn('text-sm font-medium')}>Before</span>
                    <Input
                      type="text"
                      className={cn('w-[65px]')}
                      value={field.value?.count}
                      onChange={(e) => {
                        form.setValue(field.name, {
                          ...field.value,
                          count: e.target.value,
                        })
                        markCountError(e.target.value)
                      }}
                    />
                    <Select
                      value={field.value?.unit}
                      onValueChange={(value) => {
                        form.setValue(field.name, {
                          ...field.value,
                          unit: value,
                        })
                        markUnitError(value)
                      }}
                    >
                      <SelectTrigger className={cn('w-[90px]')}>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem key="day" value="day">
                          Day
                        </SelectItem>
                        <SelectItem key="week" value="week">
                          Week
                        </SelectItem>
                        <SelectItem key="month" value="month">
                          Month
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formErrors.notificationSettings?.message && (
                    <p className={cn('text-xs font-medium text-destructive')}>
                      {formErrors.notificationSettings.message}
                    </p>
                  )}
                </FormItem>
              )}
            />
          </div>
        </Form>
      </CardContent>
    </Card>
  )
}
